import {
  BoxGeometry,
  Color,
  Group,
  Mesh,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Object3D,
} from 'three';
import { kicadToWorld } from '../model/CoordinateMapper';
import type { BoardComponent, RegionId } from '../types';

export interface SwitchVisual {
  ref: string;
  region: RegionId;
  input: 'a' | 'b';
  root: Group;
  lever: Mesh;
  hitbox: Mesh;
  target: number;
}

export function createSwitchOverlay(component: BoardComponent, region: RegionId, input: 'a' | 'b'): SwitchVisual {
  const root = new Group();
  root.name = `interactive-${component.ref}`;
  root.position.copy(kicadToWorld(component.x + 3.81, component.y, 0.012));
  root.rotation.y = -component.rotation * Math.PI / 180;

  const well = new Mesh(
    new BoxGeometry(0.009, 0.0012, 0.0045),
    new MeshStandardMaterial({ color: 0x152230, metalness: 0.55, roughness: 0.38 }),
  );
  root.add(well);

  const lever = new Mesh(
    new BoxGeometry(0.0035, 0.0024, 0.0034),
    new MeshPhysicalMaterial({
      color: 0xf2f6f9,
      roughness: 0.28,
      metalness: 0.08,
      clearcoat: 0.5,
      emissive: new Color(0x000000),
    }),
  );
  lever.position.set(-0.002, 0.0018, 0);
  root.add(lever);

  const hitbox = new Mesh(
    new BoxGeometry(0.013, 0.009, 0.009),
    new MeshStandardMaterial({ transparent: true, opacity: 0, depthWrite: false }),
  );
  hitbox.position.y = 0.002;
  hitbox.userData = { kind: 'switch', ref: component.ref, region, input };
  root.add(hitbox);

  return { ref: component.ref, region, input, root, lever, hitbox, target: -0.002 };
}

export function setSwitchState(item: SwitchVisual, on: boolean): void {
  item.target = on ? 0.002 : -0.002;
  const material = item.lever.material as MeshPhysicalMaterial;
  material.emissive.setHex(on ? 0x15572e : 0x000000);
  material.emissiveIntensity = on ? 0.8 : 0;
}

export function animateSwitch(item: SwitchVisual, delta: number): void {
  item.lever.position.x += (item.target - item.lever.position.x) * Math.min(1, delta * 15);
  item.lever.rotation.z = (item.lever.position.x / 0.002) * -0.12;
}

export function setSwitchHover(item: SwitchVisual, hovered: boolean): void {
  const material = item.lever.material as MeshPhysicalMaterial;
  material.clearcoat = hovered ? 1 : 0.5;
  item.root.scale.setScalar(hovered ? 1.12 : 1);
}

export function isSwitchHit(object: Object3D): object is Mesh {
  return object.userData.kind === 'switch';
}
